import { Components, Theme } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { typographyClasses } from '@mui/material/Typography';

// Table spec — Figma "Простая таблица" / "Большая таблица" (MP design system):
//
// * head is ALWAYS Small Bold, with 10px between the head text and its divider;
// * body rows have 20px above and below the content, with a divider under every
//   row (including the last one);
// * columns are 20px apart, with no outer padding — the first cell is flush
//   left, the last one flush right;
// * dividers are 1px Light Gray (`inputBorder`), never the paler Delimiter Gray
//   used for generic `divider` lines;
// * default cell alignment is top-left; numeric columns are right-aligned
//   (`align="right"` per usage) and get tabular figures so digits in cells above
//   and below line up;
// * row hover is Table Hover — applied only to rows that opt in with `hover`,
//   which is also how "no hover for tables on a non-white surface" is expressed;
// * the footer (totals) row keeps the 20px rhythm but has no divider under it.
//
// Not expressible here (left to the call sites): the paginator is hidden for a
// single page (`CPagination` returns null when `count < 2`), and table width /
// fade + horizontal scroll depend on the data of each individual table.
const COLUMN_GAP = '20px';
const ROW_SPACING = '20px';
const HEAD_TEXT_TO_DIVIDER = '10px';

export const MuiTable: {
  MuiTableRow: Components<Theme>['MuiTableRow'];
  MuiTableCell: Components<Theme>['MuiTableCell'];
} = {
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiTableRow-hover:hover': {
          backgroundColor: theme.palette.customColors.tableHover,
          cursor: 'pointer',
        },
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        borderBottomColor: theme.palette.customColors.inputBorder,
        verticalAlign: 'top',
        // Typography follows the table size: small → body2 (a whole table may be
        // set in Small when it carries a lot of content), medium → body1.
        // `ownerState.size` reflects the size inherited from <Table size>.
        ...(ownerState.size === 'small'
          ? theme.typography.body2
          : theme.typography.body1),
        // A table set in Small must carry its children too: a <Typography>
        // without a variant defaults to body1 and would render bigger than its
        // own cell. `:where()` keeps this at one-class specificity so an sx /
        // system prop on the Typography still wins, and an explicit variant
        // (body2, caption, h4 …) is left alone. Prefer no wrapper at all, or
        // `variant="inherit"`, when writing new cells.
        ...(ownerState.size === 'small' && {
          [`& :where(.${typographyClasses.body1})`]: {
            font: 'inherit',
            lineHeight: 'inherit',
            letterSpacing: 'inherit',
          },
        }),
        // Digits line up vertically in numeric (right-aligned) columns.
        ...(ownerState.align === 'right' && {
          fontVariantNumeric: 'tabular-nums',
        }),
        // Vertical rhythm applies to every cell, including `padding="checkbox"`
        // ones (MUI zeroes their padding); `padding="none"` opts out entirely.
        ...(ownerState.padding !== 'none' && {
          paddingTop: ROW_SPACING,
          paddingBottom: ROW_SPACING,
        }),
        // Column gap: right padding only, so content columns sit exactly 20px
        // apart and the table has no outer padding.
        ...(ownerState.padding === 'normal' && {
          paddingLeft: 0,
          paddingRight: COLUMN_GAP,
          '&:last-of-type': { paddingRight: 0 },
        }),
        [`&.${tableCellClasses.head}`]: {
          ...theme.typography.body2,
          fontWeight: theme.typography.fontWeightBold,
          paddingTop: 0,
          paddingBottom: HEAD_TEXT_TO_DIVIDER,
        },
        [`&.${tableCellClasses.footer}`]: {
          ...theme.typography.body2,
          fontWeight: theme.typography.fontWeightBold,
          color: theme.palette.text.primary,
          borderBottom: 'none',
        },
      }),
    },
  },
};
